// src/components/ui/PinDots.tsx — six-slot PIN display.
//
// Verbatim port of docs/prototype/auth.jsx lines 18-40. Two letter slots
// (positions 0 and 2, 30x30 rounded square showing the entered character)
// and four digit slots (positions 1, 3, 4, 5, 18x18 circle, filled brassDk
// when entered). Shake animation fires when `shake` is true.

import type { CSSProperties } from "react";
import { C, F } from "../../theme";

export const PIN_LENGTH = 6;
export const PIN_LETTERS = ["K", "F", "W", "T"] as const;
export const PIN_LETTER_POSITIONS = [0, 2] as const;

export type PinLetter = (typeof PIN_LETTERS)[number];

/** True when the given 0-based position is a letter slot. */
export function positionNeedsLetter(pos: number): boolean {
  return pos === 0 || pos === 2;
}

/** Validate a complete 6-character PIN: [KFWT] N [KFWT] NNN. */
export const PIN_REGEX = /^[KFWT]\d[KFWT]\d{3}$/;

export interface PinDotsProps {
  value: string;
  shake?: boolean;
}

const wrapStyle: CSSProperties = {
  display: "flex",
  gap: 10,
  justifyContent: "center",
  marginBottom: 20,
};

export const PinDots = ({ value, shake }: PinDotsProps) => (
  <>
    <div style={{ ...wrapStyle, animation: shake ? "nymr-pin-shake 0.4s ease" : "none" }}>
      {Array.from({ length: PIN_LENGTH }).map((_, i) => {
        const filled = i < value.length;
        const isLetterSlot = positionNeedsLetter(i);
        const slotStyle: CSSProperties = {
          width: isLetterSlot ? 30 : 18,
          height: 30,
          borderRadius: isLetterSlot ? 8 : "50%",
          background: filled ? C.brassDk : "transparent",
          border: `2px solid ${filled ? C.brassDk : C.brassLt}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: filled ? C.brassGlow : C.brassLt,
          fontFamily: F.serif,
          fontSize: 14,
          fontWeight: 800,
          transition: "all 0.15s",
        };
        return (
          <div key={i} style={slotStyle}>
            {filled && isLetterSlot ? value[i] : ""}
          </div>
        );
      })}
    </div>
    {/* The keyframes injection. Repeat-safe: same name across mounts. */}
    <style>{shakeKeyframes}</style>
  </>
);

const shakeKeyframes = `
@keyframes nymr-pin-shake {
  0%, 100% { transform: translateX(0); }
  20%, 60% { transform: translateX(-8px); }
  40%, 80% { transform: translateX(8px); }
}
`;
